/* ============================================================================
   ENTRY — boots the Reality Collective Service Framework, loads the ported design
   runtime in the prototype's script order, then mounts <App> inside the
   framework's <ServiceFrameworkProvider> so any component can resolve services
   via useService().

   The design modules (src/design/*.js) use the legacy global pattern
   (window.React / window.UI / window.Q ...). We expose React on window, load the
   data + components first, boot the services (which can then read the store), and
   load app.js LAST — its module body calls window.__mountYpereaApp().
   ============================================================================ */
import React from 'react';
import * as ReactDOMClient from 'react-dom/client';
import {
  ServiceManager,
  ManualScheduler,
  createBrowserEnvironment,
  ServiceFrameworkProvider,
} from './runtime';
import { buildProfile } from './boot/profile';
import { CONFIG } from './config';
import { MOCK_STORE_TOKEN, SCOPE_TOKEN } from './services/tokens';
import { AuthGate } from './auth/AuthGate';
import { HostPreview } from './live/HostPreview';

// The ported prototype reads these as globals (UMD-style).
window.React = React;
(window as unknown as { ReactDOM: unknown }).ReactDOM = ReactDOMClient;
// Live mode swaps the DATA plane (the store is hydrated from QA in AuthGate), so
// the original design screens render live data unchanged. The only extra UI is:
//  - HostPreview : the REAL design host view (window.HostApp) for the operator.
//  - __YP_LIVE   : flag the sidebar nav reads to show the Host-preview item.
(window as unknown as { HostPreview: unknown }).HostPreview = HostPreview;
(window as unknown as { __YP_LIVE: boolean }).__YP_LIVE = CONFIG.authMode === 'firebase';

async function loadDesignRuntime(): Promise<void> {
  // Order mirrors the prototype's <script> tags. app.js is intentionally excluded
  // here and imported last (it triggers the mount).
  await import('./design/theme.js');
  await import('./design/data.js');
  await import('./design/scope.js');
  await import('./design/ui.js');
  await import('./design/shell.js');
  await import('./design/screens-manage.js');
  await import('./design/calendar.js');
  await import('./design/screens-operate.js');
  await import('./design/screens-account.js');
  await import('./design/screens-finance.js');
  await import('./design/screens-notify.js');
  await import('./design/screens-branding.js');
  await import('./design/screens-architecture.js');
  await import('./design/host.js');
}

async function main(): Promise<void> {
  await loadDesignRuntime();

  // Boot the Service Framework: register + initialize the profile, then start.
  const manager = new ServiceManager({
    scheduler: new ManualScheduler(),
    environment: createBrowserEnvironment(),
  });
  manager.initializeProfile(buildProfile(CONFIG));
  manager.start();
  (window as unknown as { __yperea: unknown }).__yperea = { manager, config: CONFIG };

  if (import.meta.env.DEV) {
    // Proof the service layer is live and owns the data plane.
    const store = manager.resolve(MOCK_STORE_TOKEN);
    const scope = manager.resolve(SCOPE_TOKEN).build('usr_asha', null);
    console.info(
      '[Yperea] Service Framework v1 booted →',
      manager.getDiagnostics().services.map((s) => s.name).join(', '),
      '· mode:',
      CONFIG.mode,
      '· global KPIs:',
      store.q.kpis(scope),
    );
  }

  // Theme the early auth/splash screens (before the prototype mounts its own styles).
  const rootEl = document.getElementById('root') as HTMLElement;
  window.UI?.injectStyles?.();
  const baseVars = window.themeVars('still_water');
  Object.keys(baseVars).forEach((k) => rootEl.style.setProperty(k, baseVars[k]));
  document.body.style.background = baseVars['--bg-solid'];

  const root = ReactDOMClient.createRoot(rootEl);

  // Bridge the prototype's <App> into the provider tree, behind the AuthGate
  // (real per-user QA auth in firebase mode; pass-through in demo mode).
  window.__mountYpereaApp = (App: unknown) => {
    root.render(
      React.createElement(
        ServiceFrameworkProvider,
        {
          manager,
          children: React.createElement(AuthGate, {
            children: React.createElement(App as React.ComponentType),
          }),
        },
      ),
    );
  };

  // app.js mounts via window.__mountYpereaApp() at the end of its module body.
  await import('./design/app.js');
}

void main();
